/**
 * Incarcarea bibliotecii Google Maps, o singura data pe pagina.
 *
 * In portal statea un `<script async defer src=".../js?key=...&callback=initMap">`
 * scris in macheta, cu cheia in git. Aici scriptul se pune din cod, cu cheia
 * venita de la server, si se tine minte ca a fost pus: la a doua deschidere a
 * ecranului nu se mai cere inca o data.
 *
 * Fara `any`: din tot API-ul se folosesc doar cateva lucruri, si sunt scrise
 * aici, ca sa se vada ce anume atinge ecranul.
 */

export interface LatLngLiteral {
  lat: number
  lng: number
}

export interface GMapMarker {
  setMap(map: unknown): void
  setPosition(at: LatLngLiteral): void
  addListener(event: string, handler: () => void): void
}

export interface GMapInfoWindow {
  setContent(html: string): void
  open(map: unknown, marker: GMapMarker): void
  close(): void
}

export interface GMapPolyline {
  setMap(map: unknown): void
  setPath(path: LatLngLiteral[]): void
}

export interface GMapBounds {
  extend(point: LatLngLiteral): void
}

export interface GMap {
  setCenter(at: LatLngLiteral): void
  panTo(at: LatLngLiteral): void
  fitBounds(bounds: GMapBounds): void
}

export interface GoogleMaps {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GMap
  Marker: new (options: Record<string, unknown>) => GMapMarker
  InfoWindow: new (options: Record<string, unknown>) => GMapInfoWindow
  Polyline: new (options: Record<string, unknown>) => GMapPolyline
  LatLngBounds: new () => GMapBounds
  LatLng: new (lat: number, lng: number) => unknown
  Size: new (w: number, h: number) => unknown
  Point: new (x: number, y: number) => unknown
}

interface MapsWindow extends Window {
  google?: { maps?: GoogleMaps }
}

let pending: Promise<GoogleMaps> | null = null

export function loadGoogleMaps(key: string): Promise<GoogleMaps> {
  const scope = window as MapsWindow

  if (scope.google?.maps) return Promise.resolve(scope.google.maps)
  if (pending) return pending
  if (key === '') return Promise.reject(new Error('No Google Maps key configured.'))

  pending = new Promise<GoogleMaps>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}`
    script.async = true
    script.defer = true
    script.onload = () => {
      const maps = (window as MapsWindow).google?.maps
      if (maps) resolve(maps)
      else reject(new Error('Google Maps did not load.'))
    }
    script.onerror = () => {
      pending = null
      reject(new Error('Google Maps did not load.'))
    }
    document.head.appendChild(script)
  })

  return pending
}

/** Numai pentru probe: uita ca scriptul a fost cerut. */
export function forgetGoogleMaps(): void {
  pending = null
}
